import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useDropzone } from "react-dropzone";
import {
  Upload,
  FileText,
  CheckCircle2,
  Clock,
  AlertCircle,
  FolderOpen,
  RefreshCw,
  MoreVertical,
  Trash2,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  MessageSquare,
} from "lucide-react";
import { getAccessToken } from "../../services/authApi";
import { fetchConditions as fetchConditionsApi, deleteAttachment } from "../../services/conditionsApi";
import { fetchAttachmentDetail } from "../../services/documentsApi";
import { fieldReader } from "../../services/loanApi";
import { uploadDocument } from "../../services/brokerDocUploadApi";
import DocumentsGrid from "./DocumentsGrid";
import DocumentFileViewer from "./DocumentFileViewer";
import ConditionComments from "./ConditionComments";
import "./Conditions.css";

const IsMock = false;
const ShowDelete = false;

const ACCEPTED_FILE_TYPES = {
  "application/pdf": [".pdf"],
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
};

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB
const PAGE_SIZE = 10;

// Mock data simulating GET API response
const MOCK_CONDITIONS = [
  {
    conditionId: "1",
    conditionName: "Proof of appraisal delivery",
    conditionType: "Collateral",
    dueDate: "2024-05-20",
    priorTo: "Docs",
    status: "Needs Action",
    statusDescription: "Action required",
    conditionGroupName: "Collateral",
    conditionDescription: "Signed acknowledgment that the appraisal report was delivered to the borrower.",
    folders: [
      {
        id: "f1",
        name: "Appraisal",
        attachments: [
          { attachmentId: "a1", fileName: "appraisal_delivery.pdf", fileSize: 2516582, fileType: "pdf", uploadedOn: "2024-05-13" },
        ],
      },
      {
        id: "f2",
        name: "Appraisal Correspondence",
        attachments: [
          { attachmentId: "a2", fileName: "appraisal_email.pdf", fileSize: 1153434, fileType: "pdf", uploadedOn: "2024-05-13" },
          { attachmentId: "a3", fileName: "appraisal_followup.pdf", fileSize: 876544, fileType: "pdf", uploadedOn: "2024-05-13" },
        ],
      },
    ],
  },
  {
    conditionId: "2",
    conditionName: "Entity docs – Operating Agreement",
    conditionType: "Legal",
    dueDate: "2024-05-22",
    priorTo: "Funding",
    status: "In Review",
    statusDescription: "Under review by UW",
    conditionGroupName: "Legal",
    conditionDescription: "Full executed operating agreement for McFadden Holdings LLC.",
    folders: [
      {
        id: "f3",
        name: "Legal Documents",
        attachments: [
          { attachmentId: "a4", fileName: "operating_agreement.pdf", fileSize: 4298137, fileType: "pdf", uploadedOn: "2024-05-14" },
        ],
      },
    ],
  },
  {
    conditionId: "3",
    conditionName: "W-2 Forms (Most Recent Year)",
    conditionType: "Income",
    dueDate: "2024-05-25",
    priorTo: "Docs",
    status: "Cleared",
    statusDescription: "Completed",
    conditionGroupName: "Income",
    conditionDescription: "Provide W-2 forms for the most recent tax year.",
    folders: [
      {
        id: "f4",
        name: "Borrower W-2",
        attachments: [
          { attachmentId: "a5", fileName: "john_smith_w2.pdf", fileSize: 1258291, fileType: "pdf", uploadedOn: "2024-05-10" },
        ],
      },
      {
        id: "f5",
        name: "Co-Borrower W-2",
        attachments: [
          { attachmentId: "a6", fileName: "jane_smith_w2.pdf", fileSize: 1153434, fileType: "pdf", uploadedOn: "2024-05-10" },
        ],
      },
    ],
  },
  {
    conditionId: "4",
    conditionName: "Bank Statements – Last 2 Months",
    conditionType: "Assets",
    dueDate: "2024-05-20",
    priorTo: "Docs",
    status: "Needs Action",
    statusDescription: "Action required",
    conditionGroupName: "Assets",
    conditionDescription: "Provide the most recent 2 months of bank statements.",
    folders: [
      {
        id: "f6",
        name: "Bank Statements",
        attachments: [],
      },
    ],
  },
  {
    conditionId: "5",
    conditionName: "Title Insurance Commitment",
    conditionType: "Title",
    dueDate: "2024-05-28",
    priorTo: "Funding",
    status: "In Review",
    statusDescription: "Under review by UW",
    conditionGroupName: "Title",
    conditionDescription: "Provide the title insurance commitment showing the property is clear of liens.",
    folders: [
      {
        id: "f7",
        name: "Title Documents",
        attachments: [
          { attachmentId: "a7", fileName: "title_commitment.pdf", fileSize: 3145728, fileType: "pdf", uploadedOn: "2024-05-15" },
        ],
      },
    ],
  },
  {
    conditionId: "6",
    conditionName: "Homeowners Insurance Policy",
    conditionType: "Insurance",
    dueDate: "2024-05-30",
    priorTo: "Docs",
    status: "Needs Action",
    statusDescription: "Action required",
    conditionGroupName: "Insurance",
    conditionDescription: "Provide evidence of homeowners insurance with adequate coverage.",
    folders: [
      {
        id: "f8",
        name: "Insurance Documents",
        attachments: [],
      },
    ],
  },
  {
    conditionId: "7",
    conditionName: "Employment Verification Letter",
    conditionType: "Income",
    dueDate: "2024-05-26",
    priorTo: "Docs",
    status: "In Review",
    statusDescription: "Under review by UW",
    conditionGroupName: "Income",
    conditionDescription: "Verbal or written verification of employment from current employer.",
    folders: [
      {
        id: "f9",
        name: "Employment Docs",
        attachments: [
          { attachmentId: "a8", fileName: "voe_letter.pdf", fileSize: 524288, fileType: "pdf", uploadedOn: "2024-05-16" },
        ],
      },
    ],
  },
  {
    conditionId: "8",
    conditionName: "Gift Letter and Source Documentation",
    conditionType: "Assets",
    dueDate: "2024-05-24",
    priorTo: "Docs",
    status: "Cleared",
    statusDescription: "Completed",
    conditionGroupName: "Assets",
    conditionDescription: "Provide signed gift letter and proof of donor's ability to give.",
    folders: [
      {
        id: "f10",
        name: "Gift Documentation",
        attachments: [
          { attachmentId: "a9", fileName: "gift_letter_signed.pdf", fileSize: 412000, fileType: "pdf", uploadedOn: "2024-05-12" },
          { attachmentId: "a10", fileName: "donor_bank_statement.pdf", fileSize: 890000, fileType: "pdf", uploadedOn: "2024-05-12" },
        ],
      },
    ],
  },
  {
    conditionId: "9",
    conditionName: "Flood Certification",
    conditionType: "Collateral",
    dueDate: "2024-05-27",
    priorTo: "Funding",
    status: "Cleared",
    statusDescription: "Completed",
    conditionGroupName: "Collateral",
    conditionDescription: "Flood zone determination certificate for the subject property.",
    folders: [
      {
        id: "f11",
        name: "Flood Cert",
        attachments: [
          { attachmentId: "a11", fileName: "flood_cert.pdf", fileSize: 310000, fileType: "pdf", uploadedOn: "2024-05-11" },
        ],
      },
    ],
  },
  {
    conditionId: "10",
    conditionName: "Pay Stubs – Most Recent 30 Days",
    conditionType: "Income",
    dueDate: "2024-05-21",
    priorTo: "Docs",
    status: "Needs Action",
    statusDescription: "Action required",
    conditionGroupName: "Income",
    conditionDescription: "Provide pay stubs covering the most recent 30-day period.",
    folders: [
      {
        id: "f12",
        name: "Pay Stubs",
        attachments: [],
      },
    ],
  },
  {
    conditionId: "11",
    conditionName: "Tax Returns – 2 Years",
    conditionType: "Income",
    dueDate: "2024-05-29",
    priorTo: "Docs",
    status: "In Review",
    statusDescription: "Under review by UW",
    conditionGroupName: "Income",
    conditionDescription: "Provide signed federal tax returns for the most recent 2 years.",
    folders: [
      {
        id: "f13",
        name: "Tax Returns",
        attachments: [
          { attachmentId: "a12", fileName: "2022_tax_return.pdf", fileSize: 2097152, fileType: "pdf", uploadedOn: "2024-05-14" },
          { attachmentId: "a13", fileName: "2023_tax_return.pdf", fileSize: 2200000, fileType: "pdf", uploadedOn: "2024-05-14" },
        ],
      },
    ],
  },
  {
    conditionId: "12",
    conditionName: "Survey of Property",
    conditionType: "Collateral",
    dueDate: "2024-06-01",
    priorTo: "Funding",
    status: "Needs Action",
    statusDescription: "Action required",
    conditionGroupName: "Collateral",
    conditionDescription: "Provide a current survey of the subject property.",
    folders: [
      {
        id: "f14",
        name: "Survey Documents",
        attachments: [],
      },
    ],
  },
  {
    conditionId: "13",
    conditionName: "Divorce Decree",
    conditionType: "Legal",
    dueDate: "2024-05-23",
    priorTo: "Docs",
    status: "Cleared",
    statusDescription: "Completed",
    conditionGroupName: "Legal",
    conditionDescription: "Provide complete copy of divorce decree including all pages and exhibits.",
    folders: [
      {
        id: "f15",
        name: "Legal Documents",
        attachments: [
          { attachmentId: "a14", fileName: "divorce_decree.pdf", fileSize: 1800000, fileType: "pdf", uploadedOn: "2024-05-09" },
        ],
      },
    ],
  },
  {
    conditionId: "14",
    conditionName: "Condo HOA Certification",
    conditionType: "Collateral",
    dueDate: "2024-06-02",
    priorTo: "Funding",
    status: "In Review",
    statusDescription: "Under review by UW",
    conditionGroupName: "Collateral",
    conditionDescription: "HOA certification confirming dues, reserves, and litigation status.",
    folders: [
      {
        id: "f16",
        name: "HOA Documents",
        attachments: [
          { attachmentId: "a15", fileName: "hoa_cert.pdf", fileSize: 950000, fileType: "pdf", uploadedOn: "2024-05-17" },
        ],
      },
    ],
  },
  {
    conditionId: "15",
    conditionName: "Credit Explanation Letter",
    conditionType: "Credit",
    dueDate: "2024-05-19",
    priorTo: "Docs",
    status: "Cleared",
    statusDescription: "Completed",
    conditionGroupName: "Credit",
    conditionDescription: "Letter of explanation for recent credit inquiries and late payments.",
    folders: [
      {
        id: "f17",
        name: "Credit Documents",
        attachments: [
          { attachmentId: "a16", fileName: "credit_explanation.pdf", fileSize: 256000, fileType: "pdf", uploadedOn: "2024-05-08" },
        ],
      },
    ],
  },
  {
    conditionId: "16",
    conditionName: "Termite Inspection Report",
    conditionType: "Collateral",
    dueDate: "2024-06-03",
    priorTo: "Funding",
    status: "Needs Action",
    statusDescription: "Action required",
    conditionGroupName: "Collateral",
    conditionDescription: "Provide termite/pest inspection report for the subject property.",
    folders: [
      {
        id: "f18",
        name: "Inspection Reports",
        attachments: [],
      },
    ],
  },
];

const formatFileSize = (bytes) => {
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${bytes} B`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
};

const Conditions = () => {
  const [loanId, setLoanId] = useState("");
  const [conditions, setConditions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("All");
  const [priorToFilter, setPriorToFilter] = useState("All");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [conditionTypeFilter, setConditionTypeFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [openMenuId, setOpenMenuId] = useState(null);
  const [expandedIds, setExpandedIds] = useState([]);
  const [openCommentsId, setOpenCommentsId] = useState(null);
  const [uploadQueue, setUploadQueue] = useState([]);
  const [collapsed, setCollapsed] = useState(false);
  const [toast, setToast] = useState(null);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [importantDates, setImportantDates] = useState({});
  const [datesExpanded, setDatesExpanded] = useState(true);

  // Main tab: "Conditions" or "Documents"
  const [mainTab, setMainTab] = useState("Conditions");

  const menuRef = useRef(null);

  // Initialize loan id from Encompass context
  useEffect(() => {
    if (!IsMock && window.elli && window.elli.script && window.elli.script.guest) {
      try {
        window.elli.script.guest.create();
        window.elli.script
          .getObject("loan")
          .then((loanObject) =>
            loanObject.all().then((loan) => {
              const id = loan?.id;
              if (id) setLoanId(id);
            })
          );
      } catch (err) {
        console.error("Error initializing Encompass context:", err);
      }
    }
  }, []);

  const getAuthCode = async () => {
    const authObj = await window.elli.script.getObject("auth");
    return authObj.createAuthCode();
  };

  const getToken = useCallback(async () => {
    const authCode = await getAuthCode();
    const token = await getAccessToken(authCode);
    if (!token) throw new Error("Unable to acquire access token");
    return token;
  }, []);

  const getUserId = async () => {
    const authObj = await window.elli.script.getObject("auth");
    const user = await authObj.getUser();
    return user?.id || "";
  };

  // GET API call
  const fetchConditionsData = useCallback(async () => {
    setLoading(true);
    try {
      if (IsMock) {
        await new Promise((resolve) => setTimeout(resolve, 600));
        setConditions(MOCK_CONDITIONS);
      } else {
        const token = await getToken();
        const data = await fetchConditionsApi(loanId, token);
        setConditions(data);
      }
    } catch (error) {
      console.error("Failed to load conditions:", error);
    } finally {
      setLoading(false);
    }
  }, [loanId]);

  useEffect(() => {
    if (IsMock || loanId) {
      fetchConditionsData();
    }
  }, [loanId, fetchConditionsData]);

  // Fetch important dates via field reader
  useEffect(() => {
    const fetchImportantDates = async () => {
      if (!loanId || IsMock) return;
      try {
        const token = await getToken();
        const fieldIds = ["2301", "2302", "762", "2354", "ANALYZER.X20", "ANALYZER.X21", "ANALYZER.X19", "1034"];
        const values = await fieldReader(token, fieldIds, loanId);
        setImportantDates(values || {});
      } catch (err) {
        console.error("Failed to fetch important dates:", err);
      }
    };
    fetchImportantDates();
  }, [loanId, getToken]);

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Derive dashboard counts
  const dashboard = useMemo(() => {
    const total = conditions.length;
    const needsAction = conditions.filter((c) => c.status === "Needs Action").length;
    const inReview = conditions.filter((c) => c.status === "In Review").length;
    const cleared = conditions.filter((c) => c.status === "Cleared").length;
    return { total, needsAction, inReview, cleared };
  }, [conditions]);

  // Derive prior to options from data
  const priorToOptions = useMemo(() => {
    const options = [...new Set(conditions.map((c) => c.priorTo).filter(Boolean))];
    return ["All", ...options];
  }, [conditions]);

  // Derive sort options (condition group names) from data
  const categoryOptions = useMemo(() => {
    const groups = [...new Set(conditions.map((c) => c.conditionGroupName).filter(Boolean))];
    return ["All", ...groups];
  }, [conditions]);

  // Derive condition type options from data
  const conditionTypeOptions = useMemo(() => {
    const types = [...new Set(conditions.map((c) => c.conditionType).filter(Boolean))];
    return ["All", ...types];
  }, [conditions]);

  // Filter conditions
  const filteredConditions = useMemo(() => {
    let result = [...conditions];

    // Tab filter
    if (activeTab === "Needs Action") result = result.filter((c) => c.status === "Needs Action");
    else if (activeTab === "In Review") result = result.filter((c) => c.status === "In Review");
    else if (activeTab === "Cleared") result = result.filter((c) => c.status === "Cleared");

    // Prior to filter
    if (priorToFilter !== "All") result = result.filter((c) => c.priorTo === priorToFilter);

    // Category filter
    if (categoryFilter !== "All") result = result.filter((c) => c.conditionGroupName === categoryFilter);

    // Condition type filter
    if (conditionTypeFilter !== "All") result = result.filter((c) => c.conditionType === conditionTypeFilter);

    // Search filter
    if (appliedSearch.trim()) {
      const term = appliedSearch.trim().toLowerCase();
      result = result.filter((c) => {
        if ((c.externalId || "").toLowerCase().includes(term)) return true;
        if ((c.conditionName || "").toLowerCase().includes(term)) return true;
        if ((c.conditionDescription || "").toLowerCase().includes(term)) return true;
        const fileNames = (c.folders || []).flatMap((f) => (f.attachments || []).map((a) => (a.fileName || "").toLowerCase()));
        if (fileNames.some((fn) => fn.includes(term))) return true;
        return false;
      });
    }

    // Sorting
    if (sortColumn) {
      const dir = sortDirection === "asc" ? 1 : -1;
      result.sort((a, b) => {
        let valA, valB;
        switch (sortColumn) {
          case "externalId":
            valA = a.externalId || "";
            valB = b.externalId || "";
            return dir * valA.localeCompare(valB, undefined, { numeric: true });
          case "condition":
            valA = (a.conditionName || "").toLowerCase();
            valB = (b.conditionName || "").toLowerCase();
            return dir * valA.localeCompare(valB);
          case "category":
            valA = (a.conditionGroupName || "").toLowerCase();
            valB = (b.conditionGroupName || "").toLowerCase();
            return dir * valA.localeCompare(valB);
          case "conditionType":
            valA = (a.conditionType || "").toLowerCase();
            valB = (b.conditionType || "").toLowerCase();
            return dir * valA.localeCompare(valB);
          case "description":
            valA = (a.conditionDescription || "").toLowerCase();
            valB = (b.conditionDescription || "").toLowerCase();
            return dir * valA.localeCompare(valB);
          case "priorTo":
            valA = (a.priorTo || "").toLowerCase();
            valB = (b.priorTo || "").toLowerCase();
            return dir * valA.localeCompare(valB);
          case "status":
            valA = (a.status || "").toLowerCase();
            valB = (b.status || "").toLowerCase();
            return dir * valA.localeCompare(valB);
          case "eFolders":
            valA = (a.folders || []).length;
            valB = (b.folders || []).length;
            return dir * (valA - valB);
          case "files":
            const getLatestDate = (cond) => {
              const dates = (cond.folders || []).flatMap((f) =>
                (f.attachments || []).map((att) => new Date(att.uploadedOn || 0).getTime())
              );
              return dates.length > 0 ? Math.max(...dates) : 0;
            };
            valA = getLatestDate(a);
            valB = getLatestDate(b);
            return dir * (valA - valB);
          case "comments":
            valA = a.commentsCount || 0;
            valB = b.commentsCount || 0;
            return dir * (valA - valB);
          default:
            return 0;
        }
      });
    }

    return result;
  }, [conditions, activeTab, priorToFilter, categoryFilter, conditionTypeFilter, appliedSearch, sortColumn, sortDirection]);

  // Pagination
  const totalPages = Math.ceil(filteredConditions.length / PAGE_SIZE);
  const paginatedConditions = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredConditions.slice(start, start + PAGE_SIZE);
  }, [filteredConditions, currentPage]);

  // Reset page on filter change
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, priorToFilter, categoryFilter, conditionTypeFilter, sortColumn, sortDirection]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const renderSortIcon = (column) => {
    if (sortColumn !== column) return <ChevronDown size={12} className="cond-sort-icon-inactive" />;
    return sortDirection === "asc"
      ? <ChevronUp size={12} className="cond-sort-icon-active" />
      : <ChevronDown size={12} className="cond-sort-icon-active" />;
  };

  // Upload handler
  const handleUpload = useCallback(async (acceptedFiles) => {
    if (IsMock) {
      console.log("Uploading files:", acceptedFiles.map((f) => f.name));
      alert(`${acceptedFiles.length} file(s) uploaded successfully. Files will be matched to conditions.`);
      return;
    }

    const newQueue = acceptedFiles.map((file) => ({
      id: `upload-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
      file,
      progress: 0,
      status: "uploading",
    }));

    setUploadQueue((prev) => [...prev, ...newQueue]);

    let token;
    try {
      token = await getToken();
    } catch {
      console.error("Failed to get access token");
      setUploadQueue((prev) =>
        prev.map((q) =>
          newQueue.find((n) => n.id === q.id)
            ? { ...q, status: "error", progress: 0 }
            : q
        )
      );
      return;
    }

    for (const item of newQueue) {
      try {
        await uploadDocument(
          item.file,
          token,
          loanId,
          "",
          (progress) => {
            setUploadQueue((prev) =>
              prev.map((q) => (q.id === item.id ? { ...q, progress } : q))
            );
          }
        );

        setUploadQueue((prev) =>
          prev.map((q) => (q.id === item.id ? { ...q, status: "done", progress: 100 } : q))
        );

        setTimeout(() => {
          setUploadQueue((prev) => prev.filter((q) => q.id !== item.id));
        }, 1500);
      } catch {
        setUploadQueue((prev) =>
          prev.map((q) => (q.id === item.id ? { ...q, status: "error", progress: 0 } : q))
        );
      }
    }
  }, [loanId]);

  const onDrop = useCallback(
    (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles.length > 0) {
        const messages = rejectedFiles.map((r) => {
          const errors = r.errors.map((e) => e.message).join(", ");
          return `${r.file.name}: ${errors}`;
        });
        alert(`Some files were rejected:\n${messages.join("\n")}`);
      }
      if (acceptedFiles.length > 0) {
        handleUpload(acceptedFiles);
      }
    },
    [handleUpload]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPTED_FILE_TYPES,
    maxSize: MAX_FILE_SIZE,
    multiple: true,
  });

  // Show toast helper
  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Delete attachment
  const handleDelete = async (conditionId, attachmentId) => {
    setOpenMenuId(null);
    try {
      const token = await getToken();
      const userId = await getUserId();
      await deleteAttachment(loanId, attachmentId, token, userId);
      setConditions((prev) =>
        prev.map((cond) => {
          if (cond.conditionId !== conditionId) return cond;
          return {
            ...cond,
            folders: (cond.folders || []).map((folder) => ({
              ...folder,
              attachments: (folder.attachments || []).filter((a) => a.attachmentId !== attachmentId),
            })),
          };
        })
      );
      showToast("Attachment deleted successfully.");
    } catch (error) {
      const msg = error?.response?.data?.message || "Failed to delete attachment.";
      showToast(msg, "error");
    }
  };

  const handleFileDownload = async (attachmentId, fileName) => {
    try {
      const token = await getToken();
      const detail = await fetchAttachmentDetail(loanId, attachmentId, token);
      if (!detail?.url) return;
      const response = await fetch(detail.url, {
        headers: detail.authorizationHeader
          ? { Authorization: detail.authorizationHeader }
          : {},
      });
      if (response.ok) {
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = fileName || "document";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      }
    } catch (err) {
      console.error("Download failed:", err);
    }
  };

  const toggleExpanded = (id) => {
    setExpandedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const getTotalAttachments = (foldersArr) => {
    if (!foldersArr || !Array.isArray(foldersArr)) return 0;
    return foldersArr.reduce((sum, f) => sum + (f.attachments ? f.attachments.length : 0), 0);
  };

  return (
    <div className="cond-container">
      {/* Header */}
      <div className="cond-header">
        <div className="cond-header-left">
          <h1>Conditions</h1>
          <p>Review and manage all conditions for your loan.</p>
        </div>
        <div className="cond-header-actions">
          <button
            className="cond-collapse-btn"
            onClick={() => setCollapsed((prev) => !prev)}
            title={collapsed ? "Expand sections" : "Collapse sections"}
          >
            {collapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
          </button>
          <button className="cond-refresh-btn" onClick={fetchConditionsData} disabled={loading}>
            <RefreshCw size={14} className={loading ? "cond-icon-spin" : ""} />
            Refresh
          </button>
        </div>
      </div>

      {/* Collapsible: Dashboard + Upload */}
      {!collapsed && (
        <>
      <div className="cond-dashboard">
        <div className="cond-dashboard-card cond-card-total">
          <div className="cond-dashboard-card-icon">
            <ClipboardList size={24} />
          </div>
          <div className="cond-dashboard-card-content">
            <span className="cond-dashboard-card-count">{dashboard.total}</span>
            <span className="cond-dashboard-card-title">Total Conditions</span>
            <span className="cond-dashboard-card-desc">All conditions on this loan</span>
          </div>
        </div>
        <div className="cond-dashboard-card cond-card-needs-action">
          <div className="cond-dashboard-card-icon">
            <AlertCircle size={24} />
          </div>
          <div className="cond-dashboard-card-content">
            <span className="cond-dashboard-card-count">{dashboard.needsAction}</span>
            <span className="cond-dashboard-card-title">Needs Action</span>
            <span className="cond-dashboard-card-desc">Action required from you</span>
          </div>
        </div>
        <div className="cond-dashboard-card cond-card-in-review">
          <div className="cond-dashboard-card-icon">
            <Clock size={24} />
          </div>
          <div className="cond-dashboard-card-content">
            <span className="cond-dashboard-card-count">{dashboard.inReview}</span>
            <span className="cond-dashboard-card-title">In Review</span>
            <span className="cond-dashboard-card-desc">Underwriter is reviewing</span>
          </div>
        </div>
        <div className="cond-dashboard-card cond-card-cleared">
          <div className="cond-dashboard-card-icon">
            <CheckCircle2 size={24} />
          </div>
          <div className="cond-dashboard-card-content">
            <span className="cond-dashboard-card-count">{dashboard.cleared}</span>
            <span className="cond-dashboard-card-title">Cleared</span>
            <span className="cond-dashboard-card-desc">All requirements met</span>
          </div>
        </div>
      </div>

      {/* Important Dates */}
      <div className="cond-important-dates">
        <div className="cond-important-dates-header" onClick={() => setDatesExpanded((p) => !p)}>
          <div className="cond-important-dates-title">
            <ClipboardList size={18} />
            <span>Important Dates</span>
          </div>
          {datesExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
        {datesExpanded && (
          <div className="cond-important-dates-grid">
            <div className="cond-date-item">
              <span className="cond-date-label">Approval Date:</span>
              <span className="cond-date-value">{!importantDates["2301"] || importantDates["2301"] === "//" ? "\u2014" : importantDates["2301"]}</span>
            </div>
            <div className="cond-date-item">
              <span className="cond-date-label">Approval Exp Date:</span>
              <span className="cond-date-value">{!importantDates["2302"] || importantDates["2302"] === "//" ? "\u2014" : importantDates["2302"]}</span>
            </div>
            <div className="cond-date-item">
              <span className="cond-date-label">Rate Lock Exp:</span>
              <span className="cond-date-value">{!importantDates["762"] || importantDates["762"] === "//" ? "\u2014" : importantDates["762"]}</span>
            </div>
            <div className="cond-date-item">
              <span className="cond-date-label">Appraisal Exp Date:</span>
              <span className="cond-date-value">{!importantDates["2354"] || importantDates["2354"] === "//" ? "\u2014" : importantDates["2354"]}</span>
            </div>
            <div className="cond-date-item">
              <span className="cond-date-label">Title Exp Date:</span>
              <span className="cond-date-value">{!importantDates["1034"] || importantDates["1034"] === "//" ? "\u2014" : importantDates["1034"]}</span>
            </div>
            <div className="cond-date-item">
              <span className="cond-date-label">Income Exp Date:</span>
              <span className="cond-date-value">{!importantDates["ANALYZER.X20"] || importantDates["ANALYZER.X20"] === "//" ? "\u2014" : importantDates["ANALYZER.X20"]}</span>
            </div>
            <div className="cond-date-item">
              <span className="cond-date-label">Credit Exp Date:</span>
              <span className="cond-date-value">{!importantDates["ANALYZER.X19"] || importantDates["ANALYZER.X19"] === "//" ? "\u2014" : importantDates["ANALYZER.X19"]}</span>
            </div>
            <div className="cond-date-item">
              <span className="cond-date-label">Asset Exp Date:</span>
              <span className="cond-date-value">{!importantDates["ANALYZER.X21"] || importantDates["ANALYZER.X21"] === "//" ? "\u2014" : importantDates["ANALYZER.X21"]}</span>
            </div>
          </div>
        )}
      </div>

      {/* Upload Section */}
      <div
        {...getRootProps()}
        className={`cond-upload-section ${isDragActive ? "cond-upload-section-active" : ""}`}
      >
        <input {...getInputProps()} />
        <div className="cond-upload-left">
          <div className="cond-upload-icon">
            <Upload size={24} />
          </div>
          <div className="cond-upload-text">
            <h3>Upload documents</h3>
            <p>Drag and drop files here or browse to upload. We'll match them to your conditions.</p>
            <span>Supports: PDF, JPG, PNG &bull; Max file size: 5MB per file</span>
          </div>
        </div>
        <button type="button" className="cond-browse-btn">
          Browse Files
        </button>
      </div>

      {/* Upload Progress */}
      {uploadQueue.length > 0 && (
        <div className="cond-upload-progress-list">
          {uploadQueue.map((item) => (
            <div key={item.id} className="cond-upload-progress-item">
              <div className="cond-upload-progress-info">
                <FileText size={14} />
                <span className="cond-upload-progress-name">{item.file.name}</span>
                <span className="cond-upload-progress-pct">
                  {item.status === "error" ? "Failed" : `${item.progress}%`}
                </span>
              </div>
              <div className="cond-progress-bar-track">
                <div
                  className={`cond-progress-bar-fill ${item.status === "error" ? "cond-progress-error" : ""} ${item.status === "done" ? "cond-progress-done" : ""}`}
                  style={{ width: `${item.progress}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}
      </>
      )}

      {/* Main Tabs: Conditions | Documents */}
      <div className="cond-main-tabs">
        <button
          className={`cond-main-tab ${mainTab === "Conditions" ? "active" : ""}`}
          onClick={() => setMainTab("Conditions")}
        >
          Conditions <span className="cond-tab-count">{conditions.length}</span>
        </button>
        {/*<button
          className={`cond-main-tab ${mainTab === "Documents" ? "active" : ""}`}
          onClick={() => setMainTab("Documents")}
        >
          Documents Grid
        </button>*/}
        <button
          className={`cond-main-tab ${mainTab === "DocumentsBeta" ? "active" : ""}`}
          onClick={() => setMainTab("DocumentsBeta")}
        >
          Documents
        </button>
      </div>

      {/* Conditions Tab Content */}
      {mainTab === "Conditions" && (
      <>
      {/* Tabs and Filters */}
      <div className="cond-tabs-bar">
        <div className="cond-tabs">
          <button
            className={`cond-tab ${activeTab === "All" ? "active" : ""}`}
            onClick={() => setActiveTab("All")}
          >
            All Conditions <span className="cond-tab-count">{dashboard.total}</span>
          </button>
          <button
            className={`cond-tab ${activeTab === "Needs Action" ? "active" : ""}`}
            onClick={() => setActiveTab("Needs Action")}
          >
            Needs Action <span className="cond-tab-count">{dashboard.needsAction}</span>
          </button>
          <button
            className={`cond-tab ${activeTab === "In Review" ? "active" : ""}`}
            onClick={() => setActiveTab("In Review")}
          >
            In Review <span className="cond-tab-count">{dashboard.inReview}</span>
          </button>
          <button
            className={`cond-tab ${activeTab === "Cleared" ? "active" : ""}`}
            onClick={() => setActiveTab("Cleared")}
          >
            Cleared <span className="cond-tab-count">{dashboard.cleared}</span>
          </button>
        </div>
        <div className="cond-filter-bar">
          <div className="cond-search-wrapper">
            <input
              type="text"
              className="cond-search-input"
              placeholder="Search conditions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { setAppliedSearch(searchTerm); setCurrentPage(1); } }}
            />
            {searchTerm && (
              <button
                className="cond-search-clear"
                onClick={() => { setSearchTerm(""); setAppliedSearch(""); setCurrentPage(1); }}
              >
                &times;
              </button>
            )}
          </div>
          <select
            className="cond-filter-select"
            value={priorToFilter}
            onChange={(e) => setPriorToFilter(e.target.value)}
          >
            {priorToOptions.map((opt) => (
              <option key={opt} value={opt}>
                {opt === "All" ? "Filter By: Prior To" : opt}
              </option>
            ))}
          </select>
          <select
            className="cond-filter-select"
            value={conditionTypeFilter}
            onChange={(e) => setConditionTypeFilter(e.target.value)}
          >
            {conditionTypeOptions.map((opt) => (
              <option key={opt} value={opt}>
                {opt === "All" ? "Filter By: Condition Type" : opt}
              </option>
            ))}
          </select>
          <select
            className="cond-filter-select"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            {categoryOptions.map((opt) => (
              <option key={opt} value={opt}>
                {opt === "All" ? "Filter By: Category" : opt}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Conditions List */}
      {loading ? (
        <div className="cond-loading">
          <RefreshCw size={18} className="cond-icon-spin" style={{ marginRight: 8 }} />
          Loading conditions...
        </div>
      ) : (
        <div className="cond-list">
          {/* List Header */}
          <div className="cond-list-header">
            <span className="cond-sortable-col" onClick={() => handleSort("externalId")}>Id {renderSortIcon("externalId")}</span>
            <span className="cond-sortable-col" onClick={() => handleSort("condition")}>Condition {renderSortIcon("condition")}</span>
            <span className="cond-sortable-col" onClick={() => handleSort("description")}>Description {renderSortIcon("description")}</span>
            <span className="cond-sortable-col" onClick={() => handleSort("priorTo")}>Prior To {renderSortIcon("priorTo")}</span>
            <span className="cond-sortable-col" onClick={() => handleSort("status")}>Status {renderSortIcon("status")}</span>
            <span className="cond-sortable-col" onClick={() => handleSort("files")}>Files {renderSortIcon("files")}</span>
            <span className="cond-sortable-col" onClick={() => handleSort("comments")}>Comments {renderSortIcon("comments")}</span>
            <span></span>
          </div>

          {/* List Items */}
          {paginatedConditions.map((condition, index) => {
            const globalIndex = (currentPage - 1) * PAGE_SIZE + index + 1;
            const isExpanded = expandedIds.includes(condition.conditionId);
            const totalFiles = getTotalAttachments(condition.folders);

            return (
              <React.Fragment key={condition.conditionId}>
              <div className="cond-list-item">
                {/* Id */}
                <div className="cond-col-id">
                  {condition.externalId}
                </div>

                {/* Condition Info */}
                <div className="cond-info">
                  <div className="cond-info-top">
                    <span className="cond-info-name">
                      {condition.conditionGroupName && condition.conditionName.startsWith(condition.conditionGroupName + " - ")
                        ? condition.conditionName.slice(condition.conditionGroupName.length + 3)
                        : condition.conditionName}
                    </span>
                  </div>
                  <div className="cond-info-meta">
                    {condition.dueDate && <span>Due: {formatDate(condition.dueDate)}</span>}
                  </div>
                </div>

                {/* Description */}
                <div className="cond-col-text cond-col-description">
                  <span className={!isExpanded ? "cond-desc-clamped" : ""}>{condition.conditionDescription || "\u2014"}</span>
                  {condition.conditionDescription && condition.conditionDescription.length > 100 && (
                    <button
                      className="cond-view-more"
                      onClick={() => toggleExpanded(condition.conditionId)}
                    >
                      {isExpanded ? "View less" : "View more"}
                    </button>
                  )}
                </div>

                {/* Prior To */}
                <div className="cond-col-text">
                  {condition.priorTo ? (
                    <span className={`cond-prior-badge ${condition.priorTo === "Funding" ? "prior-funding" : ""}`}>
                      {condition.priorTo}
                    </span>
                  ) : (
                    <span>\u2014</span>
                  )}
                </div>

                {/* Status */}
                <div className="cond-status-col">
                  <span
                    className={`cond-status-badge ${
                      condition.status === "Needs Action"
                        ? "cond-status-needs-action"
                        : condition.status === "In Review"
                        ? "cond-status-in-review"
                        : "cond-status-cleared"
                    }`}
                  >
                    {condition.status}
                  </span>
                </div>

                {/* eFolders - hidden */}

                {/* Files */}
                <div className="cond-files">
                  {totalFiles > 0 ? (
                    (condition.folders || []).flatMap((folder) =>
                      (folder.attachments || []).map((att) => (
                        <div key={att.attachmentId} className="cond-file-item">
                          <FileText size={16} className="cond-file-icon" />
                          <div className="cond-file-info">
                            <span
                              className="cond-file-name cond-file-download"
                              onClick={() => handleFileDownload(att.attachmentId, att.fileName)}
                            >{att.fileName}</span>
                            <span className="cond-file-meta">
                              {formatDate(att.uploadedOn)} &bull; {formatFileSize(att.fileSize)}
                            </span>
                          </div>
                        </div>
                      ))
                    )
                  ) : null}
                </div>

                {/* Comments */}
                <div className="cond-comments-col">
                  <div
                    className={`cond-comments-badge-wrapper ${(condition.commentsCount || 0) > 0 ? "cond-comments-clickable" : ""}`}
                    onClick={() => {
                      if ((condition.commentsCount || 0) > 0) {
                        setOpenCommentsId(
                          openCommentsId === condition.conditionId ? null : condition.conditionId
                        );
                      }
                    }}
                  >
                    <MessageSquare size={18} className="cond-comments-icon" />
                    {(condition.commentsCount || 0) > 0 && (
                      <span className="cond-comments-badge">{condition.commentsCount}</span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="cond-actions" ref={openMenuId === condition.conditionId ? menuRef : null}>
                  {ShowDelete && <button
                    className="cond-actions-btn"
                    onClick={() =>
                      setOpenMenuId(
                        openMenuId === condition.conditionId ? null : condition.conditionId
                      )
                    }
                  >
                    <MoreVertical size={16} />
                  </button>}
                  {openMenuId === condition.conditionId && (
                    <div className="cond-dropdown-menu">
                      {(condition.folders || []).flatMap((f) => f.attachments || []).length > 0 ? (
                        (condition.folders || []).flatMap((f) => f.attachments || []).map((att) => (
                          <button
                            key={att.attachmentId}
                            className="cond-dropdown-item"
                            onClick={() => handleDelete(condition.conditionId, att.attachmentId)}
                          >
                            <Trash2 size={14} /> Delete {att.fileName}
                          </button>
                        ))
                      ) : (
                        <button
                          className="cond-dropdown-item"
                          style={{ color: "#94a3b8", cursor: "default" }}
                          disabled
                        >
                          No files to delete
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
              {openCommentsId === condition.conditionId && (
                <ConditionComments
                  loanId={loanId}
                  conditionId={condition.conditionId}
                  getToken={getToken}
                  onClose={() => setOpenCommentsId(null)}
                />
              )}
              </React.Fragment>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {!loading && filteredConditions.length > 0 && (
        <div className="cond-pagination">
          <span className="cond-pagination-info">
            Showing {(currentPage - 1) * PAGE_SIZE + 1} to{" "}
            {Math.min(currentPage * PAGE_SIZE, filteredConditions.length)} of{" "}
            {filteredConditions.length} conditions
          </span>
          <button
            className="cond-page-nav"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((p) => p - 1)}
          >
            <ChevronLeft size={16} />
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              className={`cond-page-btn ${currentPage === page ? "active" : ""}`}
              onClick={() => setCurrentPage(page)}
            >
              {page}
            </button>
          ))}
          <button
            className="cond-page-nav"
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((p) => p + 1)}
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}
      </>
      )}

      {/* Documents Tab Content */}
      {mainTab === "Documents" && (
        <DocumentsGrid loanId={loanId} getToken={getToken} />
      )}

      {/* Documents (Beta) Tab Content */}
      {mainTab === "DocumentsBeta" && (
        <DocumentFileViewer loanId={loanId} getToken={getToken} />
      )}

      {/* Toast notification */}
      {toast && (
        <div className={`cond-toast cond-toast-${toast.type}`}>
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default Conditions;
